import type { FileRouteTypes } from "@/routeTree.gen";
import type { InstructionsConfig } from "@/services/types";
import { isDesktop } from "./deviceType";

export type EnabledPages =
	| "home"
	| "quote-vs-estimate"
	| "device-role-instructions"
	| "prepare-scanner-device"
	| "select-scanner-device"
	| "pair-scanner-device"
	| "imei-instructions"
	| "imei-scan"
	| "max-price"
	| "instructions"
	| "device-pairing"
	| "image-capture"
	| "device-grading"
	| "offer"
	| "user-info"
	| "webapp-assessment"
	| "redirection"
	| "payment-methods"
	| "process-completed";

export type InternalStep = "" | Exclude<EnabledPages, "home">;

type RouteTo = FileRouteTypes["to"];

const STEP_ROUTE_MAP: Record<InternalStep, RouteTo> = {
	"": "/$locale/$partner",
	"quote-vs-estimate": "/$locale/$partner/quote-vs-estimate",
	"device-role-instructions": "/$locale/$partner/device-role-instructions",
	"prepare-scanner-device": "/$locale/$partner/prepare-scanner-device",
	"select-scanner-device": "/$locale/$partner/select-scanner-device",
	"pair-scanner-device": "/$locale/$partner/pair-scanner-device",
	"imei-instructions": "/$locale/$partner/imei-instructions",
	"imei-scan": "/$locale/$partner/imei-scan",
	"max-price": "/$locale/$partner/max-price",
	instructions: "/$locale/$partner/instructions",
	"device-pairing": "/$locale/$partner/device-pairing",
	"image-capture": "/$locale/$partner/image-capture",
	"device-grading": "/$locale/$partner/device-grading",
	offer: "/$locale/$partner/offer",
	"user-info": "/$locale/$partner/user-info",
	"webapp-assessment": "/$locale/$partner/webapp-assessment",
	redirection: "/$locale/$partner/redirection",
	"payment-methods": "/$locale/$partner/payment-methods",
	"process-completed": "/$locale/$partner/process-completed",
} satisfies Record<InternalStep, RouteTo>;

export function getRouteForStep(step: InternalStep): RouteTo {
	return STEP_ROUTE_MAP[step];
}

export const DEFAULT_RETAIL_STEPS = [
	"", // home
	"imei-instructions",
	"imei-scan",
	"instructions",
	"device-pairing",
	"image-capture",
	"device-grading",
	"offer",
	"webapp-assessment",
] as const;

export const DEFAULT_FROM_HOME_STEPS = [
	"",
	"quote-vs-estimate",
	"device-role-instructions",
	"select-scanner-device",
	"prepare-scanner-device",
	"imei-instructions",
	"imei-scan",
	"instructions",
	"device-pairing",
	"image-capture",
	"device-grading",
	"offer",
	"webapp-assessment",
] as const;

const mapToInternalStep = (page: EnabledPages): InternalStep => (page === "home" ? "" : page);

function removeStep(steps: InternalStep[], stepName: string): InternalStep[] {
	return steps.filter((step) => step !== stepName);
}
export function buildSteps(
	configSteps: EnabledPages[] | undefined,
	isFromHomeFlow: boolean,
	instructionsConfig?: InstructionsConfig,
): InternalStep[] {
	let steps = !configSteps?.length
		? [...(isFromHomeFlow ? DEFAULT_FROM_HOME_STEPS : DEFAULT_RETAIL_STEPS)]
		: [...new Set(configSteps.map(mapToInternalStep))];
	if (isDesktop()) steps = removeStep(steps, "select-scanner-device");
	// Drop the instructions screen entirely when the partner disabled every
	// preparation step — keeps it out of the flow and out of history.
	if (instructionsConfig && Object.values(instructionsConfig).every((enabled) => !enabled)) {
		steps = removeStep(steps, "instructions");
	}
	return steps;
}
